import { FunnelV3Step } from '../../store/funnelV3Store';

export type FunnelV3StepCategory =
    | 'question'
    | 'educational'
    | 'loading'
    | 'analysis'
    | 'conversion'
    | 'payment'
    | 'post_payment';

export type FunnelV3ProgressSection = 'about_you' | 'your_body' | 'your_plan';

interface FunnelV3StepMeta {
    category: FunnelV3StepCategory;
    section?: FunnelV3ProgressSection;
    allowBack?: boolean;
    /** Step advances on its own timer — landing here via back-navigation bounces the user forward again. */
    autoAdvances?: boolean;
}

export const FUNNEL_V3_STEP_ORDER: FunnelV3Step[] = [
    'motivation', 'motivation_intro', 'goals_intro', 'age', 'activity', 'interstitial_metabolism',
    'stress_symptoms', 'sleep', 'interstitial_sleep', 'physical_barriers', 'target_zones', 'interstitial_science',
    'height', 'current_weight', 'goal_weight', 'birth_year',
    'target_date', 'weight_projection',
    'walking_history', 'weight_pattern', 'interstitial_reframe',
    'primary_goal', 'main_barrier', 'personalized_blockers',
    'plan_loading', 'starting_point', 'plan_stages', 'commitment_check', 'expected_results', 'testimonial_review',
    'interstitial_rewards', 'reward_preference', 'review_loading', 'email_capture', 'referral_rewards',
    'journey_story', 'results',
    'paywall', 'payment',
    'create_account', 'coaching_upsell', 'exclusive_offer', 'get_app',
];

export const FUNNEL_V3_STEP_META: Record<FunnelV3Step, FunnelV3StepMeta> = {
    motivation: { category: 'question', section: 'about_you', allowBack: false },
    motivation_intro: { category: 'educational' },
    goals_intro: { category: 'educational' },
    age: { category: 'question', section: 'about_you' },
    activity: { category: 'question', section: 'about_you' },
    interstitial_metabolism: { category: 'educational', autoAdvances: true },
    stress_symptoms: { category: 'question', section: 'your_body' },
    sleep: { category: 'question', section: 'your_body' },
    interstitial_sleep: { category: 'educational' },
    physical_barriers: { category: 'question', section: 'your_body' },
    target_zones: { category: 'question', section: 'your_body' },
    interstitial_science: { category: 'educational', autoAdvances: true },
    height: { category: 'question', section: 'your_body' },
    current_weight: { category: 'question', section: 'your_body' },
    goal_weight: { category: 'question', section: 'your_body' },
    birth_year: { category: 'question', section: 'your_body' },
    target_date: { category: 'question', section: 'your_body' },
    weight_projection: { category: 'loading' },
    walking_history: { category: 'question', section: 'your_plan' },
    weight_pattern: { category: 'question', section: 'your_plan' },
    interstitial_reframe: { category: 'educational', autoAdvances: true },
    primary_goal: { category: 'question', section: 'your_plan' },
    main_barrier: { category: 'question', section: 'your_plan' },
    personalized_blockers: { category: 'analysis', autoAdvances: true },
    plan_loading: { category: 'loading' },
    starting_point: { category: 'educational' },
    plan_stages: { category: 'analysis' },
    commitment_check: { category: 'question', section: 'your_plan' },
    expected_results: { category: 'analysis' },
    testimonial_review: { category: 'analysis' },
    interstitial_rewards: { category: 'educational' },
    reward_preference: { category: 'question', section: 'your_plan' },
    review_loading: { category: 'loading' },
    email_capture: { category: 'conversion' },
    referral_rewards: { category: 'analysis' },
    journey_story: { category: 'analysis' },
    results: { category: 'analysis' },
    paywall: { category: 'conversion' },
    payment: { category: 'payment' },
    create_account: { category: 'post_payment' },
    coaching_upsell: { category: 'post_payment' },
    exclusive_offer: { category: 'post_payment' },
    get_app: { category: 'post_payment' },
};

export function getStepMeta(step: FunnelV3Step): FunnelV3StepMeta {
    return FUNNEL_V3_STEP_META[step];
}

export function isQuestionStep(step: FunnelV3Step): boolean {
    return getStepMeta(step).category === 'question';
}

export function shouldShowGlobalProgress(step: FunnelV3Step): boolean {
    // Hide the section progress bar on the very first screen (goal/motivation
    // selection) — first impression should be clean, no section labels yet.
    if (FUNNEL_V3_STEP_ORDER.indexOf(step) === 0) return false;
    return isQuestionStep(step);
}

export function shouldShowGlobalBack(step: FunnelV3Step): boolean {
    const meta = getStepMeta(step);
    if (!shouldShowGlobalProgress(step)) return false;
    if (meta.allowBack === false) return false;
    return FUNNEL_V3_STEP_ORDER.indexOf(step) > 0;
}

/**
 * Steps that must be skipped when navigating backwards: loading screens and
 * timed interstitials immediately push the user forward again, so landing on
 * them from a back gesture creates a forward-bounce trap.
 */
export function shouldSkipOnBack(step: FunnelV3Step): boolean {
    const meta = getStepMeta(step);
    return meta.category === 'loading' || meta.autoAdvances === true;
}

export function getPreviousStep(step: FunnelV3Step): FunnelV3Step | null {
    let idx = FUNNEL_V3_STEP_ORDER.indexOf(step);
    if (idx <= 0) return null;
    idx--;
    while (idx > 0 && shouldSkipOnBack(FUNNEL_V3_STEP_ORDER[idx])) {
        idx--;
    }
    if (shouldSkipOnBack(FUNNEL_V3_STEP_ORDER[idx])) return null;
    return FUNNEL_V3_STEP_ORDER[idx];
}

export function getQuestionSteps(untilStep?: FunnelV3Step): FunnelV3Step[] {
    if (!untilStep) return FUNNEL_V3_STEP_ORDER.filter(isQuestionStep);
    const untilIdx = FUNNEL_V3_STEP_ORDER.indexOf(untilStep);
    if (untilIdx < 0) return FUNNEL_V3_STEP_ORDER.filter(isQuestionStep);
    return FUNNEL_V3_STEP_ORDER
        .slice(0, untilIdx)
        .filter(isQuestionStep);
}

export function getSectionQuestionSteps(section: FunnelV3ProgressSection): FunnelV3Step[] {
    return FUNNEL_V3_STEP_ORDER.filter((step) => getStepMeta(step).section === section);
}

export function getCompletedQuestionCount(currentStep: FunnelV3Step): number {
    const questionSteps = getQuestionSteps();
    const currentQuestionIndex = questionSteps.indexOf(currentStep);
    if (currentQuestionIndex <= 0) return 0;
    return currentQuestionIndex;
}
